import { sprite } from '../kit';
import { C, Pix } from '../pix';
import { registerIcons } from '../store';
import { gem, R, ramp, type Ramp } from './craft';

/**
 * Hand-made item pictures, upper gear of the world-boss sets and the roaring-wave set: head (หัว)
 * and body (ตัว). Materials match gear-lower.ts so each set reads as one family.
 */

const fur = ramp('#c8ccd8');
const ape = ramp('#6a5a4a');
const copper = ramp('#c8743a');
const sashRed = ramp('#8a3a2a');
const ember = ramp('#ff7a2a');
const flame = ramp('#e83a22');
const scute = ramp('#5a7a5a');
const bolt = ramp('#8ad0ff');
const sea = ramp('#2a6aa8');
const foam = ramp('#d8f0f8');

type Pt = [number, number];

/** A round jingle bell: a lit sphere, its slit mouth and a ring on top. */
function jingle(p: Pix, cx: number, cy: number, r: number, m: Ramp): void {
  p.line(cx, cy - r - 3, cx, cy - r, R.wood.d1);
  gem(p, cx, cy, r, m);
  p.line(cx - r + 1, cy + 1, cx + r - 1, cy + 1, m.d2);
  p.px(cx, cy + 2, m.d2);
}

/** A scute: a small hexagon outline in the shell's shadow with a lit upper edge. */
function hex(p: Pix, cx: number, cy: number, r: number, m: Ramp): void {
  const pts: Pt[] = [];
  for (let k = 0; k <= 6; k++) {
    const a = (k * Math.PI) / 3;
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.8]);
  }
  p.path(pts, m.d2);
  p.line(cx - r * 0.4, cy - r * 0.8 + 1, cx + r * 0.4, cy - r * 0.8 + 1, m.l1);
}

/** A wave crest: a curl of foam over a dark trough, `w` wide from (x,y). */
function crest(p: Pix, x: number, y: number, n: number): void {
  for (let k = 0; k < n; k++)
    sprite(p, x + k * 6, y, ['.ff...', 'f..f..', 'f.fF.f', '.ffffF'], { f: foam.b, F: foam.l2 });
}

/** A robe from the front: shoulders, flared sleeves, crossed collar, sash and a wide hem. */
function robe(p: Pix, c: Ramp, trim: Ramp, sash: Ramp): void {
  // Sleeves hang wide and square from broad shoulders, apart from the body below the elbow.
  p.poly(
    [
      [10, 5],
      [5, 8],
      [2, 22],
      [8, 22],
      [9, 12],
    ],
    c.d1,
  );
  p.poly(
    [
      [22, 5],
      [27, 8],
      [30, 22],
      [24, 22],
      [23, 12],
    ],
    c.b,
  );
  p.poly(
    [
      [12, 4],
      [20, 4],
      [23, 8],
      [23, 15],
      [26, 28],
      [6, 28],
      [9, 15],
      [9, 8],
    ],
    c.b,
  );
  p.line(8, 13, 8, 22, c.d2).line(24, 13, 24, 22, c.d2);
  p.line(27, 8, 29, 21, c.l1);
  p.line(3, 21, 7, 21, c.d2);
  p.line(25, 21, 29, 21, c.d1);
  // Body shading: the left panel darker, a lit fold on the right.
  p.poly(
    [
      [10, 8],
      [12, 8],
      [11, 27],
      [7, 27],
      [10, 15],
    ],
    c.d1,
  );
  p.line(22, 18, 24, 27, c.l1);
  p.line(19, 18, 19, 27, c.d1);
  // Crossed collar.
  p.line(12, 4, 18, 14, trim.b);
  p.line(13, 4, 19, 13, trim.l1);
  p.line(20, 4, 16, 10, trim.d1);
  // Sash.
  p.rect(9, 14, 15, 3, sash.b);
  p.line(9, 14, 23, 14, sash.l1);
  p.line(13, 17, 12, 22, sash.d1).line(14, 17, 15, 21, sash.b);
}

registerIcons('item', {
  // ผ้าคาดเอวกระดิ่งวานร (ape set): a rust sash tied in front, three copper jingle bells on it.
  'wb-bell-sash': (p) => {
    p.path(
      [
        [2, 9],
        [9, 12],
        [16, 13],
        [23, 12],
        [30, 9],
      ],
      sashRed.d1,
      5,
    );
    p.path(
      [
        [2, 8],
        [9, 11],
        [16, 12],
        [23, 11],
        [30, 8],
      ],
      sashRed.b,
      2,
    );
    p.path(
      [
        [16, 10],
        [23, 9],
        [29, 6],
      ],
      sashRed.l1,
    );
    // The knot and its two tails.
    p.rect(13, 9, 6, 6, sashRed.b);
    p.rect(14, 10, 3, 3, sashRed.l1);
    p.thick(14, 15, 11, 25, sashRed.d1, 2);
    p.thick(17, 15, 19, 26, sashRed.b, 2);
    p.px(19, 26, sashRed.l1);
    // Silver ape fur trimming the edge.
    for (let x = 3; x < 30; x += 3) p.px(x, x < 16 ? 6 + Math.round(x / 4) : 6 + Math.round((32 - x) / 4), fur.l2);
    jingle(p, 6, 21, 3, copper);
    jingle(p, 25, 20, 3, copper);
    jingle(p, 22, 26, 2.5, copper);
  },
  // ผ้ารัดเกล้าขนเงิน: a braided headband ring of silver fur, a spray of fur rising at the front.
  'wb-ape-band': (p) => {
    p.ell(16, 18, 13, 7, ape.d1);
    for (let y = 0; y < 32; y++)
      for (let x = 0; x < 32; x++) if (((x + 0.5 - 16) / 10) ** 2 + ((y + 0.5 - 17) / 4.2) ** 2 < 1) p.erase(x, y);
    // Braid ticks round the front half.
    for (let k = 0; k < 14; k++) {
      const a = 0.15 + (k * (Math.PI - 0.3)) / 13;
      const x = 16 + Math.cos(a) * 11.5;
      const y = 18 + Math.sin(a) * 5.8;
      p.px(x, y, k % 2 ? fur.l1 : fur.b);
      p.px(x + 1, y - 1, fur.d1);
    }
    for (let k = 0; k < 10; k++) {
      const a = Math.PI + 0.3 + (k * (Math.PI - 0.6)) / 9;
      p.px(16 + Math.cos(a) * 11.5, 18 + Math.sin(a) * 5.8, fur.d1);
    }
    // The fur spray in three clumps, lit on the right.
    sprite(
      p,
      10,
      7,
      [
        '.....f......',
        '..f..ff..f..',
        '..ff.fLf.ff.',
        '.fff.fLffLf.',
        '.fdfffLffLff',
        '..dffffffff.',
        '..ddfffLff..',
        '...dddfff...',
      ],
      { f: fur.b, L: fur.l2, d: fur.d1 },
    );
    p.rect(14, 22, 4, 3, copper.b);
    p.px(17, 22, copper.l2);
    // A tail of the band hanging off the side.
    p.thick(27, 19, 29, 28, ape.b, 2);
    p.px(29, 28, fur.l1);
  },
  // ผ้าคลุมขนนกเพลิง (firebird set): a feather cape from behind, flame tips licking its hem.
  'wb-firebird-cape': (p) => {
    p.poly(
      [
        [12, 4],
        [20, 4],
        [28, 25],
        [16, 27],
        [4, 25],
      ],
      flame.d1,
    );
    p.poly(
      [
        [16, 5],
        [20, 5],
        [27, 24],
        [16, 25],
      ],
      flame.b,
    );
    // Rows of feathers, scalloped.
    for (let row = 0; row < 5; row++) {
      const y = 9 + row * 4;
      const half = 4 + row * 2.3;
      for (let x = 16 - half; x < 16 + half; x += 3) {
        const lit = x >= 16;
        p.px(x, y, lit ? flame.l1 : flame.b);
        p.px(x + 1, y + 1, lit ? ember.l1 : flame.d2);
        p.px(x + 2, y, lit ? flame.l1 : flame.b);
      }
    }
    // Flame tips along the hem.
    const tips: [number, number][] = [
      [5, 28],
      [9, 30],
      [13, 29],
      [17, 30],
      [21, 29],
      [25, 30],
      [28, 27],
    ];
    for (const [x, y] of tips) {
      p.line(x, y - 4, x, y, ember.b);
      p.px(x, y, ember.l2);
      p.px(x + 1, y - 3, ember.l1);
    }
    // Collar of gold with a clasp.
    p.rect(10, 3, 12, 3, R.gold.b);
    p.line(10, 3, 21, 3, R.gold.l2);
    gem(p, 16, 5, 2, R.red);
  },
  // มงกุฎหงอนเพลิง: a gold band crowned by a fan of flame-crest feathers.
  'wb-firebird-crown': (p) => {
    // Crest feathers, back to front.
    const feather = (x0: number, y0: number, x1: number, y1: number, m: Ramp) => {
      const L = Math.hypot(x1 - x0, y1 - y0);
      const dx = (x1 - x0) / L;
      const dy = (y1 - y0) / L;
      const at = (u: number, v: number): Pt => [x0 + dx * u - dy * v, y0 + dy * u + dx * v];
      p.poly([at(0, 0.5), at(L * 0.35, 2.4), at(L * 0.8, 2), at(L, 0), at(L * 0.8, -2), at(L * 0.35, -2.4), at(0, -0.5)], m.d1);
      p.poly([at(L * 0.3, 0), at(L * 0.8, 0), at(L * 0.95, 0), at(L * 0.8, 1.6), at(L * 0.35, 1.8)], m.b);
      p.path([at(1, 0), at(L * 0.9, 0)], m.l1);
      p.px(...at(L - 0.5, 0), ember.l2);
      p.px(...at(L * 0.55, 2.2), m.d2);
    };
    feather(9, 19, 4, 7, flame);
    feather(23, 19, 28, 7, flame);
    feather(12, 18, 10, 4, ember);
    feather(20, 18, 22, 4, ember);
    feather(16, 18, 16, 2, ramp('#ffc84a'));
    // The band, curved as seen from the front.
    p.poly(
      [
        [5, 19],
        [16, 17],
        [27, 19],
        [27, 25],
        [16, 27],
        [5, 25],
      ],
      R.gold.d1,
    );
    p.poly(
      [
        [16, 18],
        [26, 20],
        [26, 24],
        [16, 26],
      ],
      R.gold.b,
    );
    p.path(
      [
        [5, 19],
        [16, 17],
        [27, 19],
      ],
      R.gold.l2,
    );
    p.path(
      [
        [6, 25],
        [16, 27],
        [26, 25],
      ],
      R.gold.d2,
    );
    gem(p, 16, 22, 3, R.red);
    p.px(10, 22, R.gold.l1).px(22, 22, R.gold.l1);
  },
  // เกราะไหล่กระดองอสนี (turtle set): three shell lames stacked like a shoulder guard, a bolt across.
  'wb-shell-pauldron': (p) => {
    // Each lame is the top half of an ellipse; drawn bottom first so the upper ones overlap.
    const lame = (y: number, hw: number, h: number) => {
      const t = new Pix(32, 32);
      t.ell(16, y + h, hw, h, scute.d1);
      t.ell(17, y + h + 0.5, hw - 1.5, h - 1.5, scute.b);
      t.ring(16, y + h, hw - 0.5, h - 0.5, scute.l1, -1.2, -0.3);
      for (let yy = y + h + 1; yy < 32; yy++) for (let x = 0; x < 32; x++) t.erase(x, yy);
      t.rect(16 - hw + 1, y + h - 1, hw * 2 - 1, 2, R.bronze.d1);
      t.line(16, y + h - 1, 16 + hw - 1, y + h - 1, R.bronze.l1);
      p.blit(t);
    };
    lame(17, 14, 7);
    lame(11, 12, 7);
    lame(4, 10, 8);
    hex(p, 17, 8, 3.5, scute);
    hex(p, 11, 17, 3, scute);
    hex(p, 22, 16, 3, scute);
    hex(p, 16, 22, 3, scute);
    p.path(
      [
        [9, 7],
        [13, 12],
        [10, 15],
        [15, 21],
      ],
      bolt.l1,
    );
    p.px(13, 12, C.white);
    // Straps.
    p.thick(6, 24, 5, 29, R.wood.d1, 2);
    p.thick(26, 24, 27, 29, R.wood.b, 2);
    p.px(27, 29, R.bronze.l1);
  },
  // หมวกกระดองฟ้าคำราม: a domed shell helmet, scutes on the crown, flared neck guard.
  'wb-shell-helm': (p) => {
    p.ell(16, 16, 11, 11, scute.d1);
    for (let y = 16; y < 32; y++) for (let x = 0; x < 32; x++) p.erase(x, y);
    p.ell(17, 15.5, 9.5, 10, scute.b);
    for (let y = 16; y < 32; y++) for (let x = 0; x < 32; x++) p.erase(x, y);
    p.ring(16, 16, 10.5, 10.5, scute.l1, -1.7, -0.4);
    hex(p, 16, 10, 3.5, scute);
    hex(p, 10, 13, 2.8, scute);
    hex(p, 22, 13, 2.8, scute);
    // Brim band and the flared neck guard below it.
    p.poly(
      [
        [4, 16],
        [28, 16],
        [30, 25],
        [25, 23],
        [7, 23],
        [2, 25],
      ],
      ramp('#3a3a48').b,
    );
    p.rect(4, 15, 24, 3, R.bronze.b);
    p.line(4, 15, 27, 15, R.bronze.l2);
    for (const x of [8, 12, 16, 20, 24]) p.line(x, 18, x + (x - 16) * 0.15, 23, ramp('#3a3a48').d1);
    p.path(
      [
        [18, 4],
        [16, 9],
        [19, 11],
        [16, 15],
      ],
      bolt.l1,
    );
    p.px(18, 4, C.white);
  },
  // ผ้าโพกคลื่นคำราม (wave set): a sea-blue head wrap, a foam-crest badge, one tail trailing.
  'dg-set-head': (p) => {
    p.ell(16, 16, 12, 9, sea.d1);
    p.ell(17, 15, 10.5, 7.5, sea.b);
    for (let y = 22; y < 32; y++) for (let x = 0; x < 32; x++) p.erase(x, y);
    // Wrap folds sweeping across.
    for (let k = 0; k < 4; k++)
      p.path(
        [
          [5 + k, 19 - k * 3],
          [16, 17 - k * 3],
          [27 - k, 13 - k * 2],
        ],
        k % 2 ? sea.d1 : sea.l1,
      );
    p.rect(4, 19, 24, 3, sea.d2);
    p.line(4, 19, 27, 19, sea.d1);
    // The badge.
    p.ell(16, 12, 4, 3.5, R.gold.d1);
    p.ell(16, 12, 3, 2.5, foam.b);
    sprite(p, 14, 11, ['.ff.', 'f.Ff'], { f: sea.b, F: sea.l1 });
    p.px(18, 10, R.gold.l2);
    // Trailing tail.
    p.thick(26, 18, 29, 29, sea.b, 3);
    p.line(27, 18, 30, 28, sea.l1);
    crest(p, 25, 26, 1);
  },
  // เสื้อคลุมคลื่นคำราม: a sea-blue robe with a gold sash and foam waves breaking along its hem.
  'dg-set-body': (p) => {
    robe(p, sea, foam, R.gold);
    p.rect(6, 25, 21, 3, sea.d2);
    crest(p, 6, 23, 4);
    sprite(p, 2, 19, ['.f.ff', 'fffff'], { f: foam.l1 });
    sprite(p, 25, 19, ['ff.f.', 'fffff'], { f: foam.l1 });
  },
});
